/**
 * Markdown → scene. A headless port of the description renderer's
 * markdown subset (Specification §4.11.1): headings, emphasis
 * (bold/italic/strike/code), links, lists (ordered/unordered, nested),
 * GFM tables, blockquotes, fenced code blocks, horizontal rules.
 *
 * `inlineRuns` tokenizes inline markup into rich-text runs (for a
 * single-line/paragraph body). `composeMarkdown` builds a full block
 * tree of scene nodes, rendered identically by the HTML and SVG
 * backends — so a rendered description is portable + headless, not a
 * browser-only `innerHTML`.
 */
import {
  box,
  image,
  richtext,
  text,
  type BoxNode,
  type FontSpec,
  type Node,
  type Overlay,
  type TextRun,
  type TextStyle,
} from "./model.js";
import { defaultTokens, mix, type Tokens } from "./tokens.js";

const SAFE_URL_RE = /^(https?:\/\/|mailto:|archlang:\/\/|#|\.\.?\/|\/)/i;
/** Image sources: http(s), inline data:image, or a relative/absolute path. */
const SAFE_IMG_URL_RE = /^(https?:\/\/|data:image\/|\.\.?\/|\/)/i;
/** Default display box for an image with no explicit `=WxH` size hint. */
const DEFAULT_IMG_W = 320;
const DEFAULT_IMG_H = 180;

// ── Inline ───────────────────────────────────────────────────────────────

interface InlineFlags {
  readonly bold?: boolean;
  readonly italic?: boolean;
  readonly strike?: boolean;
  readonly href?: string;
}

/** Tokenize inline markdown into styled runs, recursing through nesting. */
export function inlineRuns(src: string, flags: InlineFlags = {}): TextRun[] {
  const out: TextRun[] = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf) out.push({ text: buf, ...flags });
    buf = "";
  };
  while (i < src.length) {
    const ch = src[i]!;
    // Inline code — emphasis inside is literal.
    if (ch === "`") {
      const start = i;
      while (src[i] === "`") i++;
      const ticks = i - start;
      const close = findCodeClose(src, i, ticks);
      if (close !== -1) {
        flush();
        out.push({ text: src.slice(i, close), code: true, ...flags });
        i = close + ticks;
        continue;
      }
      buf += src.slice(start, i);
      continue;
    }
    // Image `![alt](url)` — no image node in the scene model, so render the
    // alt text (keeps prose readable instead of dropping the `!`).
    if (ch === "!" && src[i + 1] === "[") {
      const img = matchLink(src, i + 1);
      if (img) {
        flush();
        out.push(...inlineRuns(img.label, flags));
        i = img.end;
        continue;
      }
    }
    // Link.
    if (ch === "[") {
      const link = matchLink(src, i);
      if (link) {
        flush();
        const href = SAFE_URL_RE.test(link.href) ? link.href : undefined;
        out.push(...inlineRuns(link.label, { ...flags, href: href ?? flags.href }));
        i = link.end;
        continue;
      }
    }
    // Sentinels: <missing:…> / <unresolved:…> / <ambiguous:…>.
    if (ch === "<") {
      const sentinel = /^<(missing|unresolved|ambiguous):([^>]*)>/.exec(src.slice(i));
      if (sentinel) {
        flush();
        out.push({ text: `<${sentinel[1]}:${sentinel[2]}>`, dim: true });
        i += sentinel[0].length;
        continue;
      }
    }
    // Emphasis.
    const emph = matchEmphasis(src, i);
    if (emph) {
      flush();
      out.push(...inlineRuns(emph.inner, { ...flags, [emph.flag]: true }));
      i = emph.end;
      continue;
    }
    buf += ch;
    i++;
  }
  flush();
  return out;
}

function findCodeClose(text: string, from: number, ticks: number): number {
  let i = from;
  while (i < text.length) {
    if (text[i] === "`") {
      const start = i;
      while (text[i] === "`") i++;
      if (i - start === ticks) return start;
    } else i++;
  }
  return -1;
}

function matchLink(text: string, i: number): { label: string; href: string; end: number } | null {
  if (text[i] !== "[") return null;
  let depth = 0;
  let j = i;
  while (j < text.length) {
    const c = text[j]!;
    if (c === "[") depth++;
    else if (c === "]") {
      depth--;
      if (depth === 0) break;
    }
    j++;
  }
  if (depth !== 0 || text[j + 1] !== "(") return null;
  const hrefStart = j + 2;
  const hrefEnd = text.indexOf(")", hrefStart);
  if (hrefEnd === -1) return null;
  return { label: text.slice(i + 1, j), href: text.slice(hrefStart, hrefEnd).trim(), end: hrefEnd + 1 };
}

function matchEmphasis(text: string, i: number): { flag: "bold" | "italic" | "strike"; inner: string; end: number } | null {
  if (text.startsWith("**", i)) {
    const close = text.indexOf("**", i + 2);
    if (close !== -1) return { flag: "bold", inner: text.slice(i + 2, close), end: close + 2 };
  }
  if (text.startsWith("__", i)) {
    const close = text.indexOf("__", i + 2);
    if (close !== -1) return { flag: "bold", inner: text.slice(i + 2, close), end: close + 2 };
  }
  if (text.startsWith("~~", i)) {
    const close = text.indexOf("~~", i + 2);
    if (close !== -1) return { flag: "strike", inner: text.slice(i + 2, close), end: close + 2 };
  }
  if ((text[i] === "*" || text[i] === "_") && text[i + 1] !== text[i] && text[i + 1] !== " ") {
    // GFM: `_` cannot open/close emphasis intra-word (snake_case stays literal);
    // `*` may. Require non-alphanumeric flanking for `_`.
    if (text[i] === "_" && isAlnum(text[i - 1])) return null;
    const close = text.indexOf(text[i]!, i + 1);
    if (close !== -1 && text[close - 1] !== " ") {
      if (text[i] === "_" && isAlnum(text[close + 1])) return null;
      return { flag: "italic", inner: text.slice(i + 1, close), end: close + 1 };
    }
  }
  return null;
}

function isAlnum(ch: string | undefined): boolean {
  return ch !== undefined && /[A-Za-z0-9]/.test(ch);
}

/**
 * Inline-only parse for a compact body: drop a leading heading line,
 * collapse to a single prose line, tokenize. Used by the module card.
 */
export function parseInlineMarkdown(md: string): TextRun[] {
  if (!md) return [];
  const s = md.replace(/^\s*#{1,6}\s+[^\n]*\n+/, "").replace(/\s+/g, " ").trim();
  return s ? inlineRuns(s) : [];
}

// ── Blocks ───────────────────────────────────────────────────────────────

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "code"; lang: string; text: string }
  | { kind: "image"; src: string; alt: string; width: number; height: number }
  | { kind: "list"; ordered: boolean; start: number; items: Block[][] }
  | { kind: "blockquote"; children: Block[] }
  | { kind: "table"; header: string[]; aligns: string[]; rows: string[][] }
  | { kind: "hr" };

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) { i++; continue; }
    const fence = /^(?:[ \t]*)(`{3,}|~{3,})\s*([^\s`~]*)\s*$/.exec(line);
    if (fence) {
      const marker = fence[1]!;
      i++;
      const start = i;
      while (i < lines.length && !lines[i]!.trim().startsWith(marker)) i++;
      blocks.push({ kind: "code", lang: fence[2] ?? "", text: lines.slice(start, i).join("\n") });
      if (i < lines.length) i++;
      continue;
    }
    if (/^\s*(?:-{3,}|_{3,}|\*{3,})\s*$/.test(line)) { blocks.push({ kind: "hr" }); i++; continue; }
    const heading = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (heading) { blocks.push({ kind: "heading", level: heading[1]!.length, text: heading[2]! }); i++; continue; }
    // Block image on its own line: `![alt](src)`, optional `"title"` and an
    // `=WxH` size hint. Unsafe srcs fall through to a paragraph (alt text).
    const img = /^\s*!\[([^\]]*)\]\(\s*([^\s)]+)(?:\s+"[^"]*")?(?:\s+=(\d+)x(\d+))?\s*\)\s*$/.exec(line);
    if (img && SAFE_IMG_URL_RE.test(img[2]!)) {
      blocks.push({
        kind: "image",
        src: img[2]!,
        alt: img[1] ?? "",
        width: img[3] ? parseInt(img[3], 10) : DEFAULT_IMG_W,
        height: img[4] ? parseInt(img[4], 10) : DEFAULT_IMG_H,
      });
      i++;
      continue;
    }
    if (/^\s{0,3}>/.test(line)) {
      const start = i;
      while (i < lines.length && /^\s{0,3}>/.test(lines[i]!)) i++;
      const inner = lines.slice(start, i).map((l) => l.replace(/^\s{0,3}>\s?/, "")).join("\n");
      blocks.push({ kind: "blockquote", children: parseBlocks(inner) });
      continue;
    }
    const tableEnd = peekTable(lines, i);
    if (tableEnd > i) {
      const header = splitTableRow(lines[i]!);
      const aligns = splitTableRow(lines[i + 1]!).map(alignFor);
      const rows: string[][] = [];
      for (let r = i + 2; r < tableEnd; r++) rows.push(splitTableRow(lines[r]!));
      blocks.push({ kind: "table", header, aligns, rows });
      i = tableEnd;
      continue;
    }
    const listMatch = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line);
    if (listMatch) {
      const baseIndent = listMatch[1]!.length;
      const ordered = listMatch[3] !== undefined;
      const start = ordered ? parseInt(listMatch[3]!, 10) : 1;
      const items: Block[][] = [];
      while (i < lines.length) {
        const m = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(lines[i]!);
        if (!m || m[1]!.length !== baseIndent) break;
        if ((m[3] !== undefined) !== ordered) break;
        // Continuation lines hang under the marker — dedent by the actual
        // content column (marker width varies: `1. ` vs `10. `).
        const contentCol = m[0]!.length - m[4]!.length;
        const itemLines: string[] = [m[4]!];
        i++;
        while (i < lines.length) {
          const next = lines[i]!;
          if (!next.trim()) { itemLines.push(""); i++; continue; }
          if (/^(\s*)(?:[-*+]|\d+[.)])\s+/.test(next)) {
            const ni = next.match(/^(\s*)/)![1]!.length;
            if (ni <= baseIndent) break;
          }
          const indent = next.match(/^(\s*)/)![1]!.length;
          if (indent <= baseIndent && next.trim().length > 0) break;
          itemLines.push(next.replace(new RegExp(`^\\s{0,${contentCol}}`), ""));
          i++;
        }
        items.push(parseBlocks(itemLines.join("\n").trim()));
      }
      blocks.push({ kind: "list", ordered, start, items });
      continue;
    }
    const paraStart = i;
    i++; // first line is non-blank + non-structural (checked above)
    let setext = 0; // 1 = `===` underline (h1), 2 = `---` underline (h2)
    while (i < lines.length) {
      const l = lines[i]!;
      if (!l.trim()) break;
      // A setext underline directly under paragraph text turns it into a
      // heading (`---` here is an underline, not a thematic break).
      if (/^\s{0,3}=+\s*$/.test(l)) { setext = 1; break; }
      if (/^\s{0,3}-+\s*$/.test(l)) { setext = 2; break; }
      if (/^\s{0,3}(#{1,6}|>|(?:_{3,}|\*{3,})\s*$|(`{3,}|~{3,}))/.test(l)) break;
      if (/^(\s*)(?:[-*+]|\d+[.)])\s+/.test(l)) break;
      if (peekTable(lines, i) > i) break; // a table interrupts the paragraph
      i++;
    }
    const paraText = lines.slice(paraStart, i).join("\n");
    if (setext) {
      blocks.push({ kind: "heading", level: setext, text: paraText.replace(/\s+/g, " ").trim() });
      i++; // consume the underline line
    } else {
      blocks.push({ kind: "paragraph", text: paraText });
    }
  }
  return blocks;
}

function peekTable(lines: readonly string[], i: number): number {
  if (i + 1 >= lines.length) return i;
  if (!/\|/.test(lines[i]!)) return i;
  if (!/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(lines[i + 1]!)) return i;
  let end = i + 2;
  while (end < lines.length && /\|/.test(lines[end]!) && lines[end]!.trim()) end++;
  return end;
}

function splitTableRow(line: string): string[] {
  return line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
}

function alignFor(spec: string): "left" | "center" | "right" | "start" {
  const s = spec.trim();
  const l = s.startsWith(":");
  const r = s.endsWith(":");
  if (l && r) return "center";
  if (r) return "right";
  if (l) return "left";
  return "start";
}

// ── Compose: Block[] → scene ──────────────────────────────────────────────

export interface MarkdownOptions {
  /** Base font size in px (default = tokens.fontSize). */
  readonly fontSize?: number;
  /** Color for body prose (default = tokens.fgSoft). */
  readonly color?: string;
}

export function composeMarkdown(md: string, t: Tokens = defaultTokens(), opts: MarkdownOptions = {}): BoxNode {
  const size = opts.fontSize ?? t.fontSize;
  const baseColor = opts.color ?? t.fgSoft;
  const fam = t.fontFamily;
  const base: FontSpec = { family: fam, size, weight: 500 };
  const baseStyle = (over: Partial<TextStyle> = {}): TextStyle => ({
    font: base, color: baseColor, align: "start", wrap: true,
    monoFamily: t.fontMono, linkColor: t.link, ...over,
  });

  const blockNode = (b: Block): Node => {
    switch (b.kind) {
      case "heading": {
        const hf: FontSpec = { family: fam, size: size * (b.level <= 1 ? 1.3 : 1.12), weight: 700 };
        return richtext(inlineRuns(b.text), baseStyle({ font: hf, color: t.fg }));
      }
      case "paragraph":
        return richtext(inlineRuns(b.text.replace(/\s+/g, " ").trim()), baseStyle());
      case "code": {
        const cf: FontSpec = { family: t.fontMono, size: size * 0.92, weight: 500 };
        return box({
          direction: "column",
          padding: [t.space2, t.space2, t.space2, t.space2],
          style: { fill: t.codeBg, radius: t.radiusSm, stroke: t.border, strokeWidth: t.borderThin },
          children: b.text.split("\n").map((ln) => text(ln || " ", { font: cf, color: t.fgSoft, align: "start" })),
        });
      }
      case "image":
        return image({ src: b.src, alt: b.alt, width: b.width, height: b.height });
      case "hr":
        return box({ direction: "row", height: t.borderThin, style: { fill: t.border }, children: [] });
      case "blockquote": {
        const bar: Overlay = {
          at: "left", stretchY: true,
          node: box({ direction: "row", width: 3, style: { fill: t.accentSoft }, children: [] }),
        };
        return box({
          direction: "column", gap: t.space1, padding: [t.space1, 0, t.space1, t.space3],
          overlays: [bar], children: b.children.map(blockNode),
        });
      }
      case "list":
        return box({
          direction: "column", gap: t.space1,
          children: b.items.map((item, idx) => {
            const marker = b.ordered ? `${b.start + idx}.` : "•";
            return box({
              direction: "row", gap: t.space2, align: "start",
              children: [
                box({ direction: "row", width: size * 1.4, children: [text(marker, { font: base, color: t.fgMuted, align: "start" })] }),
                box({ direction: "column", gap: t.space1, grow: 1, children: item.map(blockNode) }),
              ],
            });
          }),
        });
      case "table":
        return composeTable(b, base, t, baseColor);
    }
  };

  return box({
    direction: "column",
    gap: t.space2,
    children: parseBlocks(md).map(blockNode),
  });
}

function composeTable(
  b: { header: string[]; aligns: string[]; rows: string[][] },
  base: FontSpec,
  t: Tokens,
  baseColor: string,
): BoxNode {
  const cols = b.header.length;
  const cellStyle = (s: string, i: number, bold: boolean): Node =>
    box({
      // width:0 + grow:1 → every cell starts from zero and grows equally, so
      // columns line up across rows regardless of content width.
      direction: "row", grow: 1, width: 0, padding: [2, t.space2, 2, t.space2],
      style: { stroke: t.border, strokeWidth: t.borderThin },
      children: [
        richtext(inlineRuns(bold ? `**${s}**` : s), {
          font: base, color: bold ? t.fg : baseColor, monoFamily: t.fontMono, linkColor: t.link,
          align: (b.aligns[i] === "start" ? "start" : b.aligns[i]) as TextStyle["align"], wrap: true,
        }),
      ],
    });
  const row = (cells: string[], bold: boolean): BoxNode =>
    box({
      direction: "row",
      children: Array.from({ length: cols }, (_, i) => cellStyle(cells[i] ?? "", i, bold)),
    });
  return box({
    direction: "column",
    style: { fill: mix(t.codeBg, t.bg, 0.5) },
    children: [row(b.header, true), ...b.rows.map((r) => row(r, false))],
  });
}
